"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _styled = _interopRequireDefault(require("@emotion/styled"));
var _reactHookForm = require("react-hook-form");
var _Birthday = _interopRequireDefault(require("./birthday/Birthday"));
var _PasswordRequirement = _interopRequireWildcard(require("./password-requirement/PasswordRequirement"));
var _shared = require("./shared");
var _Select = _interopRequireDefault(require("../../elements/select/Select"));
var _iconAlert = _interopRequireDefault(require("../../icons/icon-alert.svg"));
var _iconEyeHidden = _interopRequireDefault(require("../../icons/icon-eye-hidden.svg"));
var _iconEyeVisible = _interopRequireDefault(require("../../icons/icon-eye-visible.svg"));
var _languageDirectionAPI = require("../../services/languageDirectionAPI");
/**
 * Validation Requirements:
 * -- Clicking submit immediately after page load should show all invalid fields red with an error message below.
 * -- Typing in an errored field should turn it from red to blue and remove the error message (the password field will retain the "helper" message).
 * -- Clicking out of a field will revalidate it.
 * -- Clicking in and out of an empty field will not result in an error.
 * -- Clearing out a field will clear an error.
 * -- Clicking submit will focus the first errored field.
 * -- Password field
 *   -- The "helper" message will be updated as the user types.
 *   -- When the field is focused: The "helper" message will remain gray while requirement has not been met. If the
 *      requirement has been met once, and is subsequently not met, the message will be red and remain so until the
 *      requirement is met again. Similarly, if submit has been clicked and the requirement is not met, the message will
 *      be red and remain so until corrected.
 */

// Hide the message if the field is empty and the user clicks out of it. Show it if the user has clicked submit.
const shouldShowErrorMessage = _ref => {
  let {
    errored,
    fieldIsDirty,
    formHasBeenSubmitted,
    type
  } = _ref;
  return errored && (fieldIsDirty && type !== 'password' || formHasBeenSubmitted);
};
const shouldDisplayInputRed = _ref2 => {
  let {
    errored,
    formHasBeenSubmitted,
    fieldIsDirty
  } = _ref2;
  return errored && (fieldIsDirty || formHasBeenSubmitted);
};
const StyledErrorMessage = _styled.default.div(_ref3 => {
  let {
    errorMessageShown,
    theme
  } = _ref3;
  return {
    display: errorMessageShown ? 'flex' : 'none',
    alignItems: 'center',
    color: theme === null || theme === void 0 ? void 0 : theme.colors.red,
    fontSize: theme === null || theme === void 0 ? void 0 : theme.fontSizes.xxxSmall,
    marginTop: '7px',
    '> img': {
      marginInlineEnd: '8px'
    }
  };
});
const StyledInputBox = _styled.default.div(_ref4 => {
  let {
    displayValidation,
    errored,
    fieldIsDirty,
    fieldIsFocused,
    inputIsRed,
    theme,
    type
  } = _ref4;
  const getInputPadding = _ref5 => {
    let {
      isDirty,
      fieldType
    } = _ref5;
    if (fieldType === 'birthday') {
      return '';
    }
    return isDirty ? '18px' : '0px';
  };
  const backgroundColor = !fieldIsFocused && inputIsRed ? theme === null || theme === void 0 ? void 0 : theme.colors.errorBackground : 'transparent';
  const borderColor = inputIsRed ? theme === null || theme === void 0 ? void 0 : theme.colors.red : theme === null || theme === void 0 ? void 0 : theme.colors.inputBorder;
  return {
    boxSizing: 'border-box',
    alignItems: 'normal',
    backgroundColor,
    border: `${errored ? '1px' : '1px'} solid ${borderColor}`,
    borderRadius: '5px',
    display: 'flex',
    height: '56px',
    padding: '1px 0px',
    position: 'relative',
    '& input': {
      paddingTop: getInputPadding({
        isDirty: fieldIsDirty,
        fieldType: type
      })
    },
    '&:focus-within': {
      border: displayValidation && inputIsRed ? `2px solid ${theme === null || theme === void 0 ? void 0 : theme.colors.red}` : `2px solid ${theme === null || theme === void 0 ? void 0 : theme.colors.inputBorderFocused}`
    }
  };
});
const StyledLabel = _styled.default.label(_ref6 => {
  let {
    direction,
    displayValidation,
    fieldIsDirty,
    fieldIsFocused,
    inputIsRed,
    theme,
    type
  } = _ref6;
  return {
    position: 'absolute',
    pointerEvents: 'none',
    [direction === 'ltr' ? 'left' : 'right']: '16px',
    top: '50%',
    transform: fieldIsDirty || fieldIsFocused && type === 'birthday' ? 'scale(0.78) translateY(-125%)' : 'translateY(-50%)',
    transformOrigin: direction === 'ltr' ? 'left' : 'right',
    transition: 'transform 0.2s ease-out',
    backgroundColor: 'transparent',
    color: displayValidation && inputIsRed ? theme === null || theme === void 0 ? void 0 : theme.colors.red : theme === null || theme === void 0 ? void 0 : theme.colors.grey[550],
    fontSize: theme === null || theme === void 0 ? void 0 : theme.fontSizes.small,
    fontWeight: theme === null || theme === void 0 ? void 0 : theme.fontWeights.regular,
    zIndex: '1'
  };
});
const StyledShowHideButton = _styled.default.button({
  alignSelf: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: '5px',
  height: '16px',
  margin: '0px 16px',
  outline: 'none',
  padding: 0,
  '&:focus': {
    outline: '1px auto',
    outlineColor: '-webkit-focus-ring-color',
    outlineOffset: '2px'
  },
  '&:hover': {
    cursor: 'pointer'
  }
});

/**
 * An input component used in forms that has validation capabilities.
 */
const Input = /*#__PURE__*/(0, _react.forwardRef)((_ref7, ref) => {
  var _typeMapping$type;
  let {
    autoFocus = false,
    clearErrors,
    control,
    disabled = false,
    errorMessage = '',
    id = '',
    initialValue = '',
    isTracking = false,
    label = '',
    options = [],
    name = '',
    passwordRequirementText = '',
    // A value in this field turns on password validation.
    placeholder = '',
    register,
    type,
    visible = true
  } = _ref7;
  const {
    direction
  } = (0, _languageDirectionAPI.useLanguageDirection)();
  const [inputType, setInputType] = (0, _react.useState)(type);
  const [fieldIsFocused, setFieldIsFocused] = (0, _react.useState)(false);
  const [passwordState, setPasswordState] = (0, _react.useState)({
    hasBeenFocused: false,
    hasMetRequirementOnce: false,
    meetsPasswordRequirements: false
  });
  const {
    isSubmitting
  } = (0, _reactHookForm.useFormState)({
    control
  });
  const {
    field
  } = (0, _reactHookForm.useController)({
    name,
    control,
    defaultValue: ''
  });
  const [fieldIsDirty, setFieldIsDirty] = (0, _react.useState)(!!(field !== null && field !== void 0 && field.value));

  // We need to know if the form has been submitted, in which case we display error messages for empty fields.
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = (0, _react.useState)(isSubmitting);
  if (isSubmitting && !formHasBeenSubmitted) {
    setFormHasBeenSubmitted(true);

    // Though the password may not have actually met the requirement, we're setting this flag so that the helper text
    // shows red if there's an error after submit and the user starts typing.
    setPasswordState(prevState => ({
      ...prevState,
      hasMetRequirementOnce: true
    }));
  }
  if (!fieldIsDirty && !!(field !== null && field !== void 0 && field.value)) {
    setFieldIsDirty(true);
  }
  const toggleType = () => {
    if (inputType === 'password') {
      setInputType('text');
    } else {
      setInputType('password');
    }
  };
  const handleFocus = () => {
    setFieldIsFocused(true);
    if (inputType === 'password') {
      setPasswordState(prevState => ({
        ...prevState,
        hasBeenFocused: true
      }));
    }
  };
  const handleBlur = e => {
    var _e$relatedTarget;
    // Don't trigger validation if we're clicking on the 'show/hide' button.
    if ((e === null || e === void 0 ? void 0 : (_e$relatedTarget = e.relatedTarget) === null || _e$relatedTarget === void 0 ? void 0 : _e$relatedTarget.id) !== 'password-toggle') {
      setFieldIsFocused(false);
    }
  };
  const handleChange = e => {
    // Hide error messages when a user is typing.
    setFormHasBeenSubmitted(false);
    setFieldIsDirty(!!e.target.value && e.target.value !== field.value);
    if (inputType === 'password') {
      const {
        length
      } = e.target.value;
      setPasswordState(prevState => ({
        ...prevState,
        meetsPasswordRequirements: length >= 8,
        hasMetRequirementOnce: length >= 8 || prevState.hasMetRequirementOnce
      }));
      if (length >= 8) {
        clearErrors === null || clearErrors === void 0 ? void 0 : clearErrors('password');
      }
    }
    if (type !== 'password') {
      clearErrors === null || clearErrors === void 0 ? void 0 : clearErrors(inputType);
    }
  };

  // Only register if the field is present (aka "visible").
  const registerProps = componentId => ({
    ...(visible && register(componentId, {
      onChange: handleChange,
      onFocus: handleFocus,
      onBlur: handleBlur
    }))
  });
  const typeMapping = {
    birthday: /*#__PURE__*/_react.default.createElement(_Birthday.default, {
      control: control,
      disabled: disabled,
      fieldIsDirty: fieldIsDirty,
      initialValue: initialValue,
      isFocused: fieldIsFocused,
      name: name,
      placeholder: placeholder,
      ref: ref,
      onBlur: handleBlur,
      onFocus: handleFocus
    }),
    input: /*#__PURE__*/_react.default.createElement(_shared.StyledInput, Object.assign({
      "aria-label": label,
      autoFocus: autoFocus,
      direction: direction,
      disabled: disabled,
      id: id,
      name: name,
      ref: ref,
      type: inputType,
      onBlur: handleBlur,
      onChange: handleChange,
      onFocus: handleFocus
    }, registerProps(id))),
    select: /*#__PURE__*/_react.default.createElement(_Select.default, {
      control: control,
      direction: direction,
      disabled: disabled,
      id: id,
      isTracking: isTracking,
      options: options,
      ref: ref,
      register: register,
      onBlur: handleBlur,
      onFocus: handleFocus
    })
  };
  const errorMessageShown = shouldShowErrorMessage({
    errored: !!errorMessage,
    fieldIsDirty,
    formHasBeenSubmitted,
    type
  });
  let inputIsRed = shouldDisplayInputRed({
    errored: !!errorMessage,
    formHasBeenSubmitted,
    fieldIsDirty
  });
  if (type === 'password') {
    const {
      meetsPasswordRequirements,
      hasBeenFocused,
      hasMetRequirementOnce
    } = passwordState;
    inputIsRed = (0, _PasswordRequirement.shouldDisplayPasswordRequirementRed)({
      errorMessageShown,
      fieldIsDirty,
      fieldIsFocused,
      meetsPasswordRequirements,
      hasBeenFocused,
      hasMetRequirementOnce
    });
  }
  return visible ? /*#__PURE__*/_react.default.createElement("div", {
    className: `input-${type}`
  }, /*#__PURE__*/_react.default.createElement(StyledInputBox, {
    className: errorMessage && 'errored-true',
    displayValidation: type !== 'password' || !!passwordRequirementText,
    errored: Boolean(errorMessage),
    fieldIsDirty: fieldIsDirty,
    fieldIsFocused: fieldIsFocused,
    id: "styled-input-box",
    inputIsRed: inputIsRed,
    type: type
  }, (_typeMapping$type = typeMapping[type]) !== null && _typeMapping$type !== void 0 ? _typeMapping$type : typeMapping.input, /*#__PURE__*/_react.default.createElement(StyledLabel, {
    direction: direction,
    displayValidation: type !== 'password' || !!passwordRequirementText,
    fieldIsDirty: fieldIsDirty || Boolean(initialValue),
    fieldIsFocused: fieldIsFocused,
    htmlFor: id,
    inputIsRed: inputIsRed,
    type: type
  }, label), type === 'password' && /*#__PURE__*/_react.default.createElement(StyledShowHideButton, {
    id: "password-toggle",
    type: "button",
    onClick: toggleType
  }, /*#__PURE__*/_react.default.createElement("img", {
    alt: "show/hide",
    src: inputType === 'password' ? _iconEyeHidden.default : _iconEyeVisible.default
  }))), errorMessage && /*#__PURE__*/_react.default.createElement(StyledErrorMessage, {
    errorMessageShown: errorMessageShown,
    id: `${id}-error`
  }, /*#__PURE__*/_react.default.createElement("img", {
    alt: "alert",
    src: _iconAlert.default
  }), /*#__PURE__*/_react.default.createElement("span", null, errorMessage)), type === 'password' && passwordRequirementText && /*#__PURE__*/_react.default.createElement(_PasswordRequirement.default, {
    errorMessageShown: errorMessageShown,
    fieldIsDirty: fieldIsDirty,
    fieldIsFocused: fieldIsFocused,
    passwordRequirementText: passwordRequirementText,
    passwordState: passwordState
  })) : null;
});
Input.displayName = 'Input';
var _default = exports.default = Input;